import React, { useState } from 'react';
import { Plus, Edit, Trash2, Tag, Calendar } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { mockPropFirms } from '@/data/mock';
import { toast } from 'sonner';

export const ExclusiveOffersPage = () => {
  const [offers, setOffers] = useState([
    {
      id: 1,
      title: 'New Year Special',
      firm: 'FTMO',
      discount: '25% OFF',
      code: 'NEWYEAR25',
      description: 'Limited time offer for new traders',
      active: true,
      expiryDate: '2026-01-31',
    },
    {
      id: 2,
      title: 'Summer Sale',
      firm: 'The5ers',
      discount: '$100 OFF',
      code: 'SUMMER100',
      description: 'Get $100 off on all challenge types',
      active: true,
      expiryDate: '2026-08-31',
    },
  ]);
  const [editingOffer, setEditingOffer] = useState(null);
  const [deleteOffer, setDeleteOffer] = useState(null);
  const [isAdding, setIsAdding] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [selectedFirm, setSelectedFirm] = useState('');
  const [discount, setDiscount] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [active, setActive] = useState(true);

  const handleAdd = () => {
    setIsAdding(true);
    resetForm();
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setTitle(offer.title);
    setSelectedFirm(offer.firm);
    setDiscount(offer.discount);
    setCode(offer.code);
    setDescription(offer.description);
    setExpiryDate(offer.expiryDate);
    setActive(offer.active);
  };

  const resetForm = () => {
    setTitle('');
    setSelectedFirm('');
    setDiscount('');
    setCode('');
    setDescription('');
    setExpiryDate('');
    setActive(true);
  };

  const handleSave = () => {
    if (!title || !selectedFirm || !discount || !code) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (editingOffer) {
      setOffers(offers.map(o => 
        o.id === editingOffer.id 
          ? { ...editingOffer, title, firm: selectedFirm, discount, code, description, expiryDate, active }
          : o
      ));
      toast.success('Offer updated successfully');
    } else {
      setOffers([...offers, {
        id: Date.now(),
        title,
        firm: selectedFirm,
        discount,
        code,
        description,
        expiryDate,
        active,
      }]);
      toast.success('Offer created successfully');
    }

    setEditingOffer(null);
    setIsAdding(false);
    resetForm();
  };

  const handleDelete = () => {
    setOffers(offers.filter(o => o.id !== deleteOffer.id));
    toast.success('Offer deleted successfully');
    setDeleteOffer(null);
  };

  return (
    <div>
      <PageHeader
        title="Exclusive Offers"
        description="Create and manage special offers and coupon codes"
        breadcrumbs={['Admin', 'Content', 'Exclusive Offers']}
      />

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold">All Offers</h3>
              <p className="text-sm text-slate-600">{offers.length} total offers</p>
            </div>
            <Button onClick={handleAdd}>
              <Plus className="h-4 w-4 mr-2" />
              Add Offer
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>OFFER</TableHead>
                <TableHead>PROP FIRM</TableHead>
                <TableHead>DISCOUNT</TableHead>
                <TableHead>CODE</TableHead>
                <TableHead>EXPIRY</TableHead>
                <TableHead>STATUS</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {offers.map((offer) => (
                <TableRow key={offer.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{offer.title}</p>
                      <p className="text-xs text-slate-500">{offer.description}</p>
                    </div>
                  </TableCell>
                  <TableCell>{offer.firm}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-700">
                      {offer.discount}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                      {offer.code}
                    </code>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <Calendar className="h-3 w-3" />
                      {offer.expiryDate || 'No expiry'}
                    </div>
                  </TableCell>
                  <TableCell>
                    {offer.active ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-slate-100 text-slate-600">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(offer)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600"
                        onClick={() => setDeleteOffer(offer)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {offers.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Tag className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No offers created yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isAdding || !!editingOffer} onOpenChange={() => {
        setIsAdding(false);
        setEditingOffer(null);
        resetForm();
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingOffer ? 'Edit Offer' : 'Create New Offer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Offer Title</Label>
              <Input
                placeholder="e.g., New Year Special"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Prop Firm</Label>
              <Select value={selectedFirm} onValueChange={setSelectedFirm}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select a prop firm" />
                </SelectTrigger>
                <SelectContent>
                  {mockPropFirms.slice(0, 10).map((firm) => (
                    <SelectItem key={firm.id} value={firm.name}>
                      {firm.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount</Label>
                <Input
                  placeholder="e.g., 25% OFF, $100 OFF"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Code</Label>
                <Input
                  placeholder="e.g., SAVE25"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description of the offer"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-2"
                rows={2}
              />
            </div>

            <div>
              <Label>Expiry Date (Optional)</Label>
              <Input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="mt-2"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Active</p>
                <p className="text-xs text-slate-500">Show this offer on the website</p>
              </div>
              <Switch checked={active} onCheckedChange={setActive} />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} className="flex-1">
                <Tag className="h-4 w-4 mr-2" />
                {editingOffer ? 'Update Offer' : 'Create Offer'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAdding(false);
                  setEditingOffer(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteOffer}
        onOpenChange={() => setDeleteOffer(null)}
        title="Delete Offer"
        description={`Are you sure you want to delete "${deleteOffer?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
};
